import React, { useState, useCallback, useEffect } from 'react';
import { Megaphone, SkipForward, BellRing, Stethoscope } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';


export default function NowServingDisplay({ showNextButton = false, onAdvanced }) {
  const [nowServing, setNowServing] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [calling, setCalling]       = useState(false);
  const [ringing, setRinging]       = useState(false);         // ringing reception bell

  const { showToast } = useToast();
  const { user } = useAuth();

  const isDoctor = user?.role === 'doctor';

  const load = useCallback(async () => {
    try {
      const res = await api.get('/patients/now-serving');
      setNowServing(res.data.nowServing);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── When doctor calls next patient ──────────────────────────────────────────
  // Reception sound/toast handled globally in AppLayout; here we handle display update + doctor sound
  useSocketEvent('queue:now-serving', (payload) => {
    const ns = payload?.nowServing || null;
    setNowServing(ns);
    if (!ns) return;


  });

  // Reception bell handled globally in AppLayout

  // ── Call next patient ────────────────────────────────────────────────────────
  async function handleCallNext() {
    setCalling(true);
    try {
      const res = await api.post('/patients/call-next');
      setNowServing(res.data.nowServing);
      showToast(res.data.message, 'success');
      onAdvanced && onAdvanced(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not call the next patient.', 'error');
    } finally { setCalling(false); }
  }

  // ── Ring reception bell ──────────────────────────────────────────────────────
  async function handleRingBell() {
    setRinging(true);
    try {
      await api.post('/patients/ring-bell');
      showToast('Reception bell sent!', 'success');
    } catch {
      showToast('Could not reach reception.', 'error');
    } finally {
      setTimeout(() => setRinging(false), 2000);
    }
  }



  if (loading) return <div className="skeleton" style={{ height: 92, borderRadius: 12 }} />;

  return (
    <div
      className="card"
      style={{
        padding: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        background: 'linear-gradient(135deg, var(--color-primary-soft), var(--color-surface))',
      }}
    >
      {/* Now serving display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 52, height: 52, borderRadius: 12,
            background: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {nowServing?.calledByPicture
            ? <img src={nowServing.calledByPicture} alt={nowServing.calledByName} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <Megaphone size={24} color="white" />}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-ink-faint)' }}>
            Now Serving
          </div>
          {nowServing?.tokenNumber ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 32, fontWeight: 800, color: 'var(--color-primary)', lineHeight: 1 }}>
                  #{nowServing.tokenNumber}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{nowServing.patientName}</div>
              </div>
              {(nowServing.calledByName || nowServing.calledByRoom) && (
                <div style={{ marginTop: 4, display:'flex', alignItems:'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-primary)' }}>
                  <Stethoscope size={13} />
                  {nowServing.calledByName && <span>Dr. {nowServing.calledByName.replace(/^dr\.?\s*/i, '')}</span>}
                  {nowServing.calledByRoom && (
                    <span style={{ padding:'1px 8px', borderRadius:100, background:'var(--color-primary-soft)', color:'var(--color-primary)', fontSize:12, fontWeight:700 }}>
                      {nowServing.calledByRoom}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-ink-faint)' }}>No patient called yet</div>
          )}
        </div>
      </div>

      {/* Doctor controls */}
      {isDoctor && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Ring reception bell */}
          <button
            className={`btn btn-sm ${ringing ? 'btn-primary' : 'btn-outline'}`}
            onClick={handleRingBell}
            disabled={ringing}
            title="Call reception (loud bell)"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <BellRing size={15} style={{ animation: ringing ? 'ring 0.4s infinite' : 'none' }} />
            {ringing ? 'Ringing…' : 'Call Reception'}
          </button>



          {/* Next patient */}
          {showNextButton && (
            <button className="btn btn-primary" onClick={handleCallNext} disabled={calling}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <SkipForward size={16} />
              {calling ? 'Calling...' : 'Next Patient'}
            </button>
          )}
        </div>
      )}

      {/* Receptionist view — no controls, just display */}
    </div>
  );
}
